/**
 * Bell-icon dropdown — shows pending-approvals count + the 3 latest
 * cards (clickable summary, project, expires_at).  The full approvals
 * list lives at /approvals (PR 12).
 */
import { Bell } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useApprovalsBell } from "@/hooks/useApprovalsBell";
import type { PendingSummaryItem } from "@/api/approvals";
import { formatDateTime } from "@/lib/datetime";
import { formatAutoSummary } from "@/lib/approvals-summary";

const VISIBLE = 3;

export function BellMenu(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const { data } = useApprovalsBell();
  const cards = data.latest.slice(0, VISIBLE);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t("layout.bellAriaLabel")}
          data-testid="bell-trigger"
          className="relative"
        >
          <Bell className="h-4 w-4" />
          {data.count > 0 && (
            <span
              data-testid="bell-count"
              className="bg-destructive text-destructive-foreground absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold"
            >
              {data.count > 99 ? "99+" : data.count}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80" data-testid="bell-menu">
        <DropdownMenuLabel>{t("layout.bellTitle", { count: data.count })}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {cards.length === 0 ? (
          <DropdownMenuItem disabled className="text-muted-foreground text-sm">
            {t("layout.bellEmpty")}
          </DropdownMenuItem>
        ) : (
          cards.map((item) => (
            <BellCard key={item.simulation_id} item={item} locale={i18n.resolvedLanguage} />
          ))
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/approvals" className="w-full text-sm">
            {t("layout.bellSeeAll")}
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function BellCard({
  item,
  locale,
}: {
  item: PendingSummaryItem;
  locale?: string;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <DropdownMenuItem asChild>
      <Link
        to="/approvals/$simulationId"
        params={{ simulationId: item.simulation_id }}
        className="flex w-full flex-col items-start gap-0.5 py-2"
        data-testid={`bell-card-${item.simulation_id}`}
      >
        <span className="text-sm font-medium">{formatAutoSummary(item, t)}</span>
        <span className="text-muted-foreground text-xs">
          {formatDateTime(item.expires_at, locale)}
        </span>
      </Link>
    </DropdownMenuItem>
  );
}
